import { css, keyframes } from "dx-styles"
import type { FC } from "react"

const slump = keyframes({
  "0%, 100%": { transform: "translateY(0)" },
  "50%": { transform: "translateY(5px)" },
})

const lidDroop = keyframes({
  "0%, 100%": { transform: "scaleY(1)" },
  "46%": { transform: "scaleY(1.32)" },
})

const tailFlop = keyframes({
  "0%, 100%": { transform: "rotate(0deg)" },
  "50%": { transform: "rotate(-3deg)" },
})

const settle = keyframes({
  "0%, 100%": { transform: "rotate(0deg)" },
  "50%": { transform: "rotate(1.5deg)" },
})

const beaver = css({
  position: "relative",
  display: "block",
  width: "100%",
  height: "100%",
  overflow: "visible",
})

const beaverSlump = css({
  transformBox: "fill-box",
  transformOrigin: "center",
  animation: `${slump} 6.6s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const lid = css({
  transformBox: "fill-box",
  transformOrigin: "top center",
  animation: `${lidDroop} 7.2s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const lidEnd = css({ animationDelay: "90ms" })

const tail = css({
  transformBox: "fill-box",
  transformOrigin: "right center",
  animation: `${tailFlop} 7.6s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const hat = css({
  transformBox: "fill-box",
  transformOrigin: "bottom center",
  animation: `${settle} 8.2s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

export const BeaverIllustration: FC = () => (
  <svg
    viewBox="0 0 220 220"
    className={beaver}
    aria-hidden="true"
  >
    <defs>
      <linearGradient
        id="agentsBeaverErrorBody"
        x1="0"
        y1="0"
        x2="0"
        y2="1"
      >
        <stop
          offset="0%"
          stopColor="#9C7458"
        />
        <stop
          offset="55%"
          stopColor="#7A5A44"
        />
        <stop
          offset="100%"
          stopColor="#4E382A"
        />
      </linearGradient>
      <linearGradient
        id="agentsHardHatError"
        x1="0"
        y1="0"
        x2="0"
        y2="1"
      >
        <stop
          offset="0%"
          stopColor="#E0A356"
        />
        <stop
          offset="100%"
          stopColor="#C97C12"
        />
      </linearGradient>
      <linearGradient
        id="agentsMuzzleError"
        x1="0"
        y1="0"
        x2="0"
        y2="1"
      >
        <stop
          offset="0%"
          stopColor="#F6EEE4"
        />
        <stop
          offset="100%"
          stopColor="#E5D3BE"
        />
      </linearGradient>
    </defs>

    <g className={beaverSlump}>
      <ellipse
        cx="110"
        cy="196"
        rx="48"
        ry="8"
        fill="rgba(70, 40, 83, 0.12)"
      />

      <g className={tail}>
        <g transform="translate(0 14) rotate(10 46 170)">
          <ellipse
            cx="46"
            cy="170"
            rx="28"
            ry="15"
            transform="rotate(-18 46 170)"
            fill="#5E3F2C"
          />
          <path
            d="M28 166 L60 174 M32 158 L62 166 M26 175 L56 182"
            stroke="rgba(255, 255, 255, 0.18)"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
        </g>
      </g>

      <path
        d="M110 96 C144 96 162 122 162 150 C162 176 140 190 110 190 C80 190 58 176 58 150 C58 122 76 96 110 96 Z"
        fill="url(#agentsBeaverErrorBody)"
      />
      <ellipse
        cx="110"
        cy="158"
        rx="31"
        ry="27"
        fill="rgba(255, 255, 255, 0.12)"
      />

      <circle
        cx="68"
        cy="74"
        r="11"
        fill="#5E3F2C"
      />
      <circle
        cx="152"
        cy="74"
        r="11"
        fill="#5E3F2C"
      />

      <circle
        cx="110"
        cy="86"
        r="42"
        fill="url(#agentsBeaverErrorBody)"
      />

      <ellipse
        cx="110"
        cy="104"
        rx="27"
        ry="21"
        fill="url(#agentsMuzzleError)"
      />
      <ellipse
        cx="110"
        cy="94"
        rx="7"
        ry="5"
        fill="#3A2145"
      />
      <rect
        x="101"
        y="110"
        width="8"
        height="19"
        rx="2"
        fill="#FFFFFF"
      />
      <path
        d="M111 110 L119 110 L119 123 L115 129 L113 121 L111 126 Z"
        fill="#FFFFFF"
      />

      <circle
        cx="94"
        cy="74"
        r="9"
        fill="#FFFFFF"
      />
      <circle
        cx="95"
        cy="76"
        r="5"
        fill="#0C1436"
      />
      <circle
        cx="93"
        cy="78"
        r="1.8"
        fill="#FFFFFF"
      />
      <path
        className={lid}
        d="M85 74 A9 9 0 0 1 103 74 L103 75 L85 75 Z"
        fill="url(#agentsBeaverErrorBody)"
      />

      <circle
        cx="126"
        cy="74"
        r="9"
        fill="#FFFFFF"
      />
      <circle
        cx="125"
        cy="76"
        r="5"
        fill="#0C1436"
      />
      <circle
        cx="123"
        cy="78"
        r="1.8"
        fill="#FFFFFF"
      />
      <path
        className={`${lid} ${lidEnd}`}
        d="M117 74 A9 9 0 0 1 135 74 L135 75 L117 75 Z"
        fill="url(#agentsBeaverErrorBody)"
      />

      <g className={hat}>
        <g transform="rotate(-7 110 40)">
          <path
            d="M68 58 C68 30 87 14 110 14 C133 14 152 30 152 58 Z"
            fill="url(#agentsHardHatError)"
          />
          <path
            d="M110 19 L110 58"
            stroke="rgba(78, 56, 42, 0.25)"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M92 18 L98 30 L88 38 L96 48 L90 58"
            stroke="rgba(78, 56, 42, 0.55)"
            strokeWidth="2.5"
            strokeLinejoin="round"
            fill="none"
          />
          <ellipse
            cx="110"
            cy="58"
            rx="50"
            ry="9"
            fill="url(#agentsHardHatError)"
          />
          <ellipse
            cx="110"
            cy="56"
            rx="50"
            ry="7"
            fill="rgba(255, 255, 255, 0.18)"
          />
        </g>
      </g>

      <ellipse
        cx="64"
        cy="152"
        rx="12"
        ry="10"
        fill="#5E3F2C"
      />

      <rect
        x="150"
        y="168"
        width="64"
        height="15"
        rx="4"
        fill="#9A6529"
      />
      <path
        d="M158 173 L206 173 M158 178 L198 178"
        stroke="rgba(78, 56, 42, 0.3)"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M178 167 L183 175 L176 184"
        stroke="#4E382A"
        strokeWidth="2.5"
        strokeLinejoin="round"
        fill="none"
      />

      <g transform="rotate(14 186 160)">
        <rect
          x="170"
          y="152"
          width="35"
          height="16"
          rx="5"
          fill="#8F93A3"
        />
        <rect
          x="170"
          y="152"
          width="35"
          height="6"
          rx="3"
          fill="rgba(255, 255, 255, 0.3)"
        />
      </g>

      <g transform="rotate(-72 150 168)">
        <rect
          x="145"
          y="146"
          width="9"
          height="44"
          rx="4"
          fill="#4E382A"
        />
        <path
          d="M145 146 L148 140 L150.5 146 L153 139 L154 146 Z"
          fill="#7A5A44"
        />
      </g>
      <ellipse
        cx="157"
        cy="158"
        rx="12"
        ry="10"
        fill="#5E3F2C"
      />
    </g>
  </svg>
)
